import React from "react";
import t from "prop-types";
import styled from "styled-components";
import {
  Area,
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { injectSimpleMovingAvg, injectBBands } from "helpers/CoronaHelpersNew";
import { abbreviateNumber } from "helpers/miscHelpers";
import useClientRect from "hooks/useClientRect";

const colors = ["#81AF43", "#FC633D", "#9D57FF", "#F03E88"];

const StyledMultiLineChart = styled.div`
  g.recharts-layer text.recharts-label tspan {
    fill: #888;
    font-weight: 600;
    text-rendering: optimizeLegibility;
    font-size: 11px;
    text-transform: uppercase;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
      Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    text-align: center;
  }

  g.recharts-layer text.recharts-text tspan {
    font-size: 12px;
    fill: #555;
  }
`;

const StyledLegend = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 0;
  margin: 0;
  align-items: center;
  justify-content: center;

  li {
    margin: 0 14px 0 0;
    font-size: 12px;
    line-height: 1;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    &:last-of-type {
      margin: 0;
    }

    span {
      display: inline-block;
      width: 14px;
      height: 14px;
      margin-right: 3px;
    }
  }
`;

const MultiLineGraph = ({
  aspectRatio,
  data,
  customTicks = null,
  interval,
  xField,
  yFields,
  scale,
  showGrid,
  small,
  smaLength,
  showSMA,
  showBBands,
  tickCount,
  tooltipFormatter,
  tooltipLabel,
  yAxisLabel,
  yTickFormatter,
}) => {
  const [ref, rect] = useClientRect();

  const margins = small
    ? { top: 10, right: 10, bottom: 15, left: 20 }
    : { top: 10, right: 10, bottom: 15, left: 30 };

  injectSimpleMovingAvg(data, smaLength);
  injectBBands(data, smaLength);
  // console.log("data here: ", data);

  const renderLegend = (props) => {
    const { payload } = props;
    // console.log("payload: ", payload);

    return (
      <StyledLegend>
        {payload
          .filter((x) => !x.dataKey.includes("_"))
          .map((entry, i) => {
            if (entry.value === "") {
              return null;
            }

            return (
              <li key={`item-${i}`}>
                <span style={{ background: colors[i] }} /> {entry.value}
              </li>
            );
          })}
      </StyledLegend>
    );
  };

  return (
    <div ref={ref}>
      {rect && rect.width ? (
        <StyledMultiLineChart>
          <ComposedChart
            name="Coronavirus Charts"
            width={rect.width}
            height={rect.width * aspectRatio}
            data={data}
            margin={margins}
          >
            {showGrid && (
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(100,100,100,0.3)"
              />
            )}
            <XAxis
              dataKey={xField}
              type="category"
              tick={{ angle: -45, offset: 5, dy: 10 }}
              height={50}
            />
            <YAxis
              label={{
                value: yAxisLabel,
                angle: -90,
                position: "center",
                dx: -30,
                dy: 0,
                textAnchor: "start",
                verticalAnchor: "start",
              }}
              width={35}
              padding={{ bottom: 10 }}
              tick={{ angle: -15 }}
              tickFormatter={yTickFormatter}
            />
            <Legend content={renderLegend} />
            />
            <Tooltip
              formatter={(value, name, props) => {
                const label = tooltipLabel || name;
                const val = tooltipFormatter(value);
                return [val, label];
              }}
              contentStyle={{ padding: 0, background: "#fcfcfc" }}
              wrapperStyle={{ padding: 0 }}
              itemStyle={{ padding: "3px 5px" }}
              labelStyle={{
                background: "#ddd",
                margin: 0,
                padding: "3px 5px",
                fontWeight: 600,
                color: "#111",
                textTransform: "uppercase",
              }}
              animationDuration={400}
            />
            {yFields &&
              yFields.map((yf, i) => (
                <Line
                  key={`line-${i}`}
                  type="monotone"
                  dataKey={yf}
                  stroke={colors[i]}
                  strokeWidth={4}
                  activeDot={{ r: 5 }}
                  dot={false}
                />
              ))}
            {yFields &&
              showSMA > 0 &&
              yFields.map((yf) => (
                <Line
                  key={`sma_${yf}`}
                  type="monotone"
                  dataKey={`sma_${yf}`}
                  stroke="red"
                  strokeWidth={3}
                  strokeDasharray="2 2"
                  activeDot={{ r: 5 }}
                  dot={false}
                />
              ))}
            {yFields &&
              showSMA > 0 &&
              showBBands > 0 &&
              yFields.map((yf) => (
                <Area
                  key={`bbands_${yf}`}
                  type="monotone"
                  dataKey={`bbands_${yf}`}
                  stroke="rgba(80,80,150,0.8)"
                  fill="rgba(80,80,200,0.15)"
                  strokeDasharray="1 2"
                  // activeDot={{ r: 5 }}
                  dot={false}
                />
              ))}
          </ComposedChart>
        </StyledMultiLineChart>
      ) : null}
    </div>
  );
};

MultiLineGraph.propTypes = {
  aspectRatio: t.number,
  customTicks: t.array,
  data: t.arrayOf(t.object).isRequired,
  interval: t.oneOfType([t.number, t.string]),
  scale: t.oneOf(["linear", "log"]),
  showGrid: t.bool,
  showBBands: t.number,
  showSMA: t.number,
  smaLength: t.number,
  small: t.bool,
  tickCount: t.number,
  tooltipFormatter: t.func,
  tooltipLabel: t.string,
  xField: t.string,
  yAxisLabel: t.string,
  yFields: t.arrayOf(t.string),
  yTickFormatter: t.func,
};

MultiLineGraph.defaultProps = {
  aspectRatio: 0.85,
  scale: "linear",
  showGrid: true,
  showSMA: 0,
  showBBands: 0,
  small: false,
  smaLength: 15,
  tickCount: 10,
  tooltipFormatter: (d) => abbreviateNumber(d, 2),
  tooltipLabel: null,
  xField: "Time",
  yAxisLabel: "Infected",
  yFields: ["PopTotal"],
  yTickFormatter: (d) => abbreviateNumber(d, 2),
};

const MemoizedMultiLineGraph = React.memo(MultiLineGraph);
export default MemoizedMultiLineGraph;
// export default MultiLineGraph;
